import { CompletionMessage } from '@app-libs/model';

export interface MessageFormatOptions {
  maxFilenameLength?: number;
}

/**
 * Formats a filename to a specified maximum length
 * @param filename - The filename to format
 * @param maxLength - Maximum length before truncation (default: 40)
 * @returns Formatted filename with ellipsis if truncated
 */
export function formatFilename(filename?: string, maxLength = 40): string {
  if (!filename) {
    return '';
  }
  
  if (filename.length > maxLength) {
    return filename.substring(0, maxLength) + '...';
  }
  
  return filename;
}

/**
 * Filters out system messages from a message array
 * @param messages - Array of messages to filter
 * @returns Array of messages without system messages
 */
export function filterSystemMessages(messages: Partial<CompletionMessage>[]): CompletionMessage[] {
  return messages.filter(
    (message) => message.role !== 'system'
  ) as CompletionMessage[];
}

/**
 * Checks if a message is from an assistant
 * @param message - Message to check
 * @returns True if message role is 'assistant'
 */
export function isAssistantMessage(message: CompletionMessage): boolean {
  return message.role === 'assistant';
}

/**
 * Checks if a message is from a user
 * @param message - Message to check
 * @returns True if message role is 'user'
 */
export function isUserMessage(message: CompletionMessage): boolean {
  return message.role === 'user';
}

/**
 * Extracts navigation route from a welcome message link
 * @param href - The href attribute value
 * @returns The route string or null if not a navigation link
 */
export function extractNavigationRoute(href?: string): string | null {
  if (!href || !href.startsWith('#navigate:')) {
    return null;
  }
  
  return href.replace('#navigate:', '');
}

/**
 * Checks if an element is a navigation link
 * @param element - The HTML element to check
 * @returns True if element is an anchor with navigation href
 */
export function isNavigationLink(element: HTMLElement): boolean {
  return (
    element.tagName === 'A' &&
    element.getAttribute('href')?.startsWith('#navigate:') === true
  );
}

/**
 * Extracts Mermaid diagram content from a message
 * @param content - The message content
 * @returns Array of Mermaid diagram strings found in the content
 */
export function extractMermaidDiagrams(content: string): string[] {
  const mermaidBlocks: string[] = [];
  const regex = /```mermaid\n([\s\S]*?)```/g;
  let match;
  
  while ((match = regex.exec(content)) !== null) {
    mermaidBlocks.push(match[1].trim());
  }
  
  return mermaidBlocks;
}

/**
 * Checks if content contains Mermaid diagrams
 * @param content - The message content
 * @returns True if content contains Mermaid blocks
 */
export function containsMermaidDiagrams(content: string): boolean {
  return /```mermaid\n[\s\S]*?```/.test(content);
}

/**
 * Strips Mermaid blocks from content (useful during streaming)
 * @param content - The message content
 * @returns Content with Mermaid blocks replaced by placeholder
 */
export function stripMermaidBlocks(content: string): string {
  return content.replace(/```mermaid\n[\s\S]*?```/g, '```\n[Mermaid diagram rendering...]\n```');
}

/**
 * Detects if content has incomplete code blocks (useful during streaming)
 * @param content - The message content
 * @returns True if there's an unclosed code block
 */
export function hasIncompleteCodeBlock(content: string): boolean {
  const codeBlockMatches = content.match(/```/g) || [];
  return codeBlockMatches.length % 2 !== 0;
}

/**
 * Gets a typing indicator message based on elapsed time
 * @param baseMessage - Base message to display (default: 'Thinking')
 * @param elapsedSeconds - Seconds elapsed since typing started
 * @returns Formatted typing message
 */
export function getTypingMessage(baseMessage = 'Thinking', elapsedSeconds = 0): string {
  const dots = '.'.repeat((elapsedSeconds % 3) + 1);
  return baseMessage + dots;
}